import { ref, readonly, onMounted, onUnmounted, computed } from 'vue';

import { useContentStore } from '@/stores/content';

/**
 * Fast connectivity signal from `navigator.onLine` plus window online/offline events.
 * Use to disable POST-only actions; keep messaging minimal (see `common.connectionRequired` in i18n).
 */
export function useConnectivity() {
  const online = ref(typeof navigator !== 'undefined' ? navigator.onLine : true);

  function sync() {
    online.value = navigator.onLine;
  }

  onMounted(() => {
    window.addEventListener('online', sync);
    window.addEventListener('offline', sync);
  });

  onUnmounted(() => {
    window.removeEventListener('online', sync);
    window.removeEventListener('offline', sync);
  });

  return {
    isOnline: readonly(online),
  };
}

/**
 * Block POST-style mutations when offline or when the current published route only has a listing
 * shell (detail sync pending — same idea as `PageView`’s listing-shell connection notice).
 */
export function useMutationConnectivity() {
  const { isOnline } = useConnectivity();
  const contentStore = useContentStore();
  const mutationsBlocked = computed(
    () => !isOnline.value || contentStore.isCurrentPageShellOnly
  );
  return { isOnline, mutationsBlocked };
}
